import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, AlertCircle, CheckCircle, Network, MapPin, Calendar, Loader2, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { 
  getTemplateTree, 
  setupMilestonesForSegment, 
  getMilestoneTreeBySegment,
  updateMilestoneBobot,
  updateMilestoneDates,
  updateMilestoneVolume
} from '../../services/milestoneService';

/**
 * SegmentMilestoneForm - INTEGRATED VERSION
 * 
 * Features:
 * - Load milestone template from backend API
 * - Display hierarchical milestone structure
 * - Allow customization of bobot, dates, volume
 * - Submit to backend for milestone setup
 * - Validation: Total bobot must = 100%
 * 
 * API Integration:
 * - GET /api/milestones/templates/tree/FO - Load template structure
 * - POST /api/segments/{id}/milestones/setup - Setup milestones
 * - GET /api/segments/{id}/milestones - Load existing (if any)
 */
const SegmentMilestoneForm = ({ segment, project, onBack, onSave }) => {
  // State
  const [milestones, setMilestones] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('create'); // 'create' or 'edit'

  // Load milestone template on mount
  useEffect(() => {
    loadMilestoneData();
  }, [segment.id]);

  /**
   * Load milestone data
   * Check if segment already has milestones (edit mode)
   * If not, load from template (create mode)
   */
  const loadMilestoneData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // First, check if segment already has milestones
      // ✅ FIX: Use tree endpoint to get hierarchical structure
      const existingMilestones = await getMilestoneTreeBySegment(segment.id);

      if (existingMilestones && existingMilestones.length > 0) {
        // EDIT MODE: Load existing milestones
        console.log('📝 Edit mode: Loading existing milestones');
        setMode('edit');
        
        // Convert ProjectMilestone to template format for display
        const convertedMilestones = convertProjectMilestonesToTemplate(existingMilestones);
        setMilestones(convertedMilestones);
        
        toast.success('Loaded existing milestones for editing');
      } else {
        // CREATE MODE: Load from template
        console.log('✨ Create mode: Loading from template');
        setMode('create');
        
        const templateTree = await getTemplateTree('FO');
        
        // Convert template to editable format with customization fields
        const editableMilestones = convertTemplateToEditable(templateTree);
        setMilestones(editableMilestones);
        
        toast.success('Loaded milestone template');
      }
    } catch (err) {
      console.error('❌ Error loading milestone data:', err);
      setError('Failed to load milestone data');
      toast.error('Failed to load milestone data');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Convert template tree to editable format
   * Add fields for customization: customBobot, planStartDate, planFinishDate
   */
  const convertTemplateToEditable = (templates) => {
    return templates.map(template => ({
      // Template fields
      templateId: template.id,
      id: template.id, // For UI key
      itemCode: template.itemCode,
      name: template.milestoneName,
      level: template.level,
      isGroup: template.isGroup,
      
      // Evidence info
      evidence: template.evidenceDescription || '',
      evidenceDefinitions: template.evidenceDefinitions || [],
      jumlahEvidence: template.evidenceDefinitions?.length > 0 
        ? (template.evidenceDefinitions[0].minFileCount || 'n') 
        : 'n',
      
      // Bobot fields
      bobotDefault: template.defaultBobot || 0,
      bobot: template.defaultBobot || 0, // Will be customized by user
      customBobot: null, // User override
      
      // Calculation method
      calculationMethod: template.calculationMethod,
      volumeUnit: template.volumeUnit,
      
      // Date fields (empty for new setup)
      planStartDate: '',
      planFinishDate: '',
      
      // Children (recursive)
      children: template.children ? convertTemplateToEditable(template.children) : []
    }));
  };

  /**
   * Convert ProjectMilestone tree (from API) to template format for editing
   * API already returns hierarchical structure, just need to convert field names
   */
  const convertProjectMilestonesToTemplate = (projectMilestones) => {
    console.log('📥 Converting', projectMilestones.length, 'milestone trees');
    
    const convertMilestone = (pm) => {
      const milestone = {
        // Template fields
        templateId: pm.templateId,
        id: pm.id,
        itemCode: pm.itemCode,
        name: pm.milestoneName,
        level: pm.level,
        isGroup: pm.isGroup,
        
        // Evidence info
        evidence: pm.evidenceDescription || '',
        evidenceDefinitions: pm.evidenceDefinitions || [],
        jumlahEvidence: pm.evidenceDefinitions?.length > 0 
          ? (pm.evidenceDefinitions[0].minFileCount || 'n') 
          : 'n',
        
        // Bobot fields
        bobotDefault: pm.defaultBobot || 0,  // Template default (for reference)
        bobot: pm.effectiveBobot || pm.defaultBobot || 0,  // Current effective value
        originalBobot: pm.effectiveBobot || pm.defaultBobot || 0,  // ✅ Store original for comparison
        customBobot: pm.overriddenBobot,  // Current override (if any)
        
        // Calculation method
        calculationMethod: pm.calculationMethod,
        volumeUnit: pm.volumeUnit,
        
        // Date fields
        planStartDate: pm.planStartDate || '',
        planFinishDate: pm.planFinishDate || '',
        
        // Parent reference (for reference only, tree already built)
        parentId: pm.parentId,
        
        // Children (recursively convert)
        children: []
      };
      
      // Recursively convert children if they exist
      if (pm.children && pm.children.length > 0) {
        milestone.children = pm.children.map(convertMilestone);
        console.log(`  ↳ ${pm.itemCode} has ${pm.children.length} children`);
      }
      
      return milestone;
    };
    
    const roots = projectMilestones.map(convertMilestone);
    
    console.log('✅ Converted', roots.length, 'root milestones');
    
    // Debug: Log tree structure
    const debugTree = (items, level = 0) => {
      items.forEach(item => {
        console.log('  '.repeat(level) + item.itemCode, '-', item.name, 
                    `(children: ${item.children.length})`);
        if (item.children.length > 0) {
          debugTree(item.children, level + 1);
        }
      });
    };
    
    console.log('🌲 Tree structure:');
    debugTree(roots);
    
    return roots;
  };

  /**
   * Handle bobot change
   */
  const handleBobotChange = (id, value) => {
    const updateMilestone = (items) => {
      return items.map(item => {
        if (item.id === id) {
          const newBobot = parseFloat(value) || 0;
          const originalBobot = parseFloat(item.originalBobot || item.bobot) || 0;
          
          console.log(`📝 Bobot change for milestone ${id}:`, {
            value,
            newBobot,
            originalBobot,
            isDifferent: newBobot !== originalBobot,
            willSetCustomBobot: newBobot
          });
          
          return { 
            ...item,
            bobot: newBobot,  // ✅ Update for display
            customBobot: newBobot  // ✅ Set for change detection
            // originalBobot stays unchanged for comparison
          };
        }
        if (item.children && item.children.length > 0) {
          return { ...item, children: updateMilestone(item.children) };
        }
        return item;
      });
    };
    setMilestones(updateMilestone(milestones));
  };

  /**
   * Handle jumlah evidence change
   */
  const handleJumlahEvidenceChange = (id, value) => {
    const updateMilestone = (items) => {
      return items.map(item => {
        if (item.id === id) {
          return { ...item, jumlahEvidence: value };
        }
        if (item.children && item.children.length > 0) {
          return { ...item, children: updateMilestone(item.children) };
        }
        return item;
      });
    };
    setMilestones(updateMilestone(milestones));
  };

  /**
   * Handle date change
   */
  const handleDateChange = (id, field, value) => {
    const updateMilestone = (items) => {
      return items.map(item => {
        if (item.id === id) {
          return { ...item, [field]: value };
        }
        if (item.children && item.children.length > 0) {
          return { ...item, children: updateMilestone(item.children) };
        }
        return item;
      });
    };
    setMilestones(updateMilestone(milestones));
  };

  /**
   * Calculate total bobot from leaf milestones
   */
  const calculateTotal = () => {
    let total = 0;
    const calcBobot = (items) => {
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          calcBobot(item.children);
        } else {
          total += item.bobot || 0;
        }
      });
    };
    calcBobot(milestones);
    return total;
  };

  /**
   * Build customizations array for API request
   */
  const buildCustomizations = () => {
    const customizations = [];

    const collectCustomizations = (items) => {
      items.forEach(item => {
        // Only include if there are customizations
        const hasCustomBobot = item.customBobot !== null && item.customBobot !== item.bobotDefault;
        const hasCustomDates = item.planStartDate || item.planFinishDate;

        if (hasCustomBobot || hasCustomDates) {
          customizations.push({
            templateId: item.templateId,
            customBobot: hasCustomBobot ? item.customBobot : null,
            bobotOverrideReason: hasCustomBobot ? 'Adjusted by PM' : null,
            planStartDate: item.planStartDate || null,
            planFinishDate: item.planFinishDate || null
          });
        }

        // Recurse children
        if (item.children && item.children.length > 0) {
          collectCustomizations(item.children);
        }
      });
    };

    collectCustomizations(milestones);
    return customizations;
  };

  /**
   * Handle form submit
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate total bobot
    const total = calculateTotal();
    if (Math.abs(total - 100) > 0.01) { // Allow small rounding error
      toast.error(`Total bobot harus 100%. Saat ini: ${total.toFixed(2)}%`);
      return;
    }

    setIsSubmitting(true);

    try {
      if (mode === 'create') {
        // CREATE MODE: Setup new milestones
        const customizations = buildCustomizations();

        const request = {
          segmentId: segment.id,
          projectType: 'FO',
          milestoneCustomizations: customizations
        };

        console.log('📤 Submitting milestone setup:', request);

        await setupMilestonesForSegment(request);
        
        toast.success('✅ Milestone setup berhasil!');
        
        // Call onSave callback with segment ID
        if (onSave) {
          await onSave(segment.id);
        }
      } else {
        // EDIT MODE: Update existing milestones
        console.log('📝 Edit mode - updating milestones');
        
        // Collect all milestones (including children) into flat list
        const allMilestones = [];
        const collectMilestones = (items) => {
          items.forEach(item => {
            allMilestones.push(item);
            if (item.children && item.children.length > 0) {
              collectMilestones(item.children);
            }
          });
        };
        collectMilestones(milestones);
        
        // Track changes to update
        const updates = {
          bobot: [],
          dates: [],
          volume: []
        };
        
        // Compare with original and collect changes
        console.log('🔍 Analyzing milestones for changes...');
        console.log('Total milestones to check:', allMilestones.length);
        
        // Log ALL milestones data first
        console.log('📊 All milestones data:');
        allMilestones.forEach((m, idx) => {
          console.log(`  ${idx + 1}. ID=${m.id} ${m.itemCode}:`, {
            bobot: m.bobot,
            bobotDefault: m.bobotDefault,
            customBobot: m.customBobot,
            isGroup: m.isGroup
          });
        });
        
        allMilestones.forEach((milestone, index) => {
          console.log(`\n📋 Checking milestone ${index + 1}/${allMilestones.length}:`, {
            id: milestone.id,
            itemCode: milestone.itemCode,
            name: milestone.milestoneName,
            isGroup: milestone.isGroup,
            customBobot: milestone.customBobot,
            bobot: milestone.bobot,
            bobotDefault: milestone.bobotDefault
          });
          
          if (!milestone.id) {
            console.log('  ⏭️  Skip: No ID');
            return;
          }
          
          // ✅ Skip group milestones (cannot update bobot for groups)
          if (milestone.isGroup) {
            console.log('  ⏭️  Skip: Is group milestone');
            return;
          }
          
          // Check bobot change - only if user explicitly customized it
          // customBobot is set when user changes the value in form
          if (milestone.customBobot !== undefined && milestone.customBobot !== null) {
            const newBobot = parseFloat(milestone.customBobot);
            const originalBobot = parseFloat(milestone.originalBobot || 0);  // ✅ Compare with ORIGINAL!
            const currentBobot = parseFloat(milestone.bobot || 0);  // Current display value
            const templateDefault = parseFloat(milestone.bobotDefault || 0);
            
            console.log('  📊 Bobot comparison:', {
              customBobot: milestone.customBobot,
              newBobot,
              originalBobot,
              currentBobot,
              templateDefault,
              isDifferent: newBobot !== originalBobot,
              isValid: newBobot > 0 && !isNaN(newBobot)
            });
            
            // ✅ Only update if actually different from ORIGINAL effective AND > 0
            if (newBobot !== originalBobot && newBobot > 0 && !isNaN(newBobot)) {
              updates.bobot.push({
                id: milestone.id,
                data: {
                  milestoneId: milestone.id,
                  newBobot: newBobot,
                  reason: 'Adjusted by PM'
                }
              });
              console.log(`  ✅ WILL UPDATE bobot: ${originalBobot}% → ${newBobot}%`);
            } else {
              console.log('  ⏭️  Skip: No change or invalid value');
            }
          } else {
            console.log('  ⏭️  Skip: customBobot is null/undefined (not customized)');
          }
          
          // Check dates change (only for leaf milestones with actual date values)
          if (!milestone.isGroup) {
            const hasStartDate = milestone.planStartDate && milestone.planStartDate.trim() !== '';
            const hasFinishDate = milestone.planFinishDate && milestone.planFinishDate.trim() !== '';
            
            if (hasStartDate || hasFinishDate) {
              updates.dates.push({
                id: milestone.id,
                data: {
                  milestoneId: milestone.id,
                  planStartDate: hasStartDate ? milestone.planStartDate : null,
                  planFinishDate: hasFinishDate ? milestone.planFinishDate : null
                }
              });
              console.log(`  📅 WILL UPDATE dates:`, {
                start: milestone.planStartDate,
                finish: milestone.planFinishDate
              });
            }
          }
        });
        
        console.log('📊 Updates to apply:', {
          bobotUpdates: updates.bobot.length,
          datesUpdates: updates.dates.length
        });
        
        // Apply updates sequentially
        let successCount = 0;
        let errorCount = 0;
        const errors = [];
        
        // Update bobots
        for (const update of updates.bobot) {
          try {
            console.log(`📤 Sending bobot update request for milestone ${update.id}:`, {
              url: `/api/milestones/${update.id}/bobot`,
              method: 'PUT',
              payload: JSON.stringify(update.data, null, 2)
            });
            
            await updateMilestoneBobot(update.id, update.data);
            successCount++;
            console.log(`✅ Bobot updated for milestone ${update.id}`);
          } catch (err) {
            console.error(`❌ Failed to update bobot for milestone ${update.id}:`, {
              error: err.message,
              response: err.response?.data,
              status: err.response?.status,
              requestData: update.data
            });
            errorCount++;
            errors.push({
              id: update.id,
              type: 'bobot',
              message: err.response?.data?.message || err.message
            });
          }
        }
        
        // Update dates
        for (const update of updates.dates) {
          try {
            console.log(`Updating dates for milestone ${update.id}:`, update.data);
            await updateMilestoneDates(update.id, update.data);
            successCount++;
            console.log(`✅ Dates updated for milestone ${update.id}`);
          } catch (err) {
            console.error(`❌ Failed to update dates for milestone ${update.id}:`, {
              error: err.message,
              response: err.response?.data,
              status: err.response?.status
            });
            errorCount++;
            errors.push({
              id: update.id,
              type: 'dates',
              message: err.response?.data?.message || err.message
            });
          }
        }
        
        console.log('✅ Update results:', { successCount, errorCount, errors });
        
        if (errorCount > 0) {
          // Show detailed error
          const errorMsg = errors.length > 0 ? 
            `Errors: ${errors.map(e => `Milestone ${e.id}: ${e.message}`).join(', ')}` :
            `${errorCount} milestones failed to update`;
          
          console.error('Update errors:', errors);
          toast.error(`Sebagian milestone gagal diupdate. ${errorMsg}`);
        } else if (successCount > 0) {
          toast.success(`✅ ${successCount} milestone berhasil diupdate!`);
        } else {
          toast.success('ℹ️ Tidak ada perubahan untuk disimpan');
        }
        
        // Refresh and go back
        if (onSave) {
          await onSave(segment.id);
        }
      }
    } catch (err) {
      console.error('❌ Error saving milestone:', err);
      toast.error(err.response?.data?.message || 'Failed to save milestone');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Render milestone row (recursive)
   */
  const renderMilestone = (item, level = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const paddingLeft = level * 24;

    return (
      <React.Fragment key={item.id}>
        <tr className={`${
          level === 0 ? 'bg-green-50' : 
          level === 1 ? 'bg-white' : 
          'bg-gray-50'
        } border-b border-gray-200 hover:bg-gray-100 transition-colors`}>
          {/* NO */}
          <td className="px-4 py-3 text-sm" style={{ paddingLeft: `${paddingLeft + 16}px` }}>
            <span className={`${level === 0 ? 'font-bold text-gray-900' : 'text-gray-700'}`}>
              {item.itemCode}.
            </span>
          </td>

          {/* MILESTONE NAME */}
          <td className="px-4 py-3 text-sm">
            <span className={`${level === 0 ? 'font-bold text-gray-900' : 'text-gray-700'}`}>
              {item.name}
            </span>
          </td>

          {/* EVIDENCE */}
          <td className="px-4 py-3 text-sm text-gray-600">
            {item.evidenceDefinitions && item.evidenceDefinitions.length > 0 ? (
              <div className="space-y-1">
                {item.evidenceDefinitions.map((ev, idx) => (
                  <div key={idx} className="text-xs">
                    {ev.evidenceName}
                    {ev.description && (
                      <div className="text-gray-500">{ev.description}</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="whitespace-pre-line text-xs">{item.evidence || '-'}</div>
            )}
          </td>

          {/* JUMLAH EVIDENCE */}
          <td className="px-4 py-3 text-sm">
            {!hasChildren && (
              <input
                type="text"
                value={item.jumlahEvidence}
                onChange={(e) => handleJumlahEvidenceChange(item.id, e.target.value)}
                className="w-20 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500 text-center"
                disabled={mode === 'edit'}
              />
            )}
          </td>

          {/* BOBOT */}
          <td className="px-4 py-3 text-sm">
            {!hasChildren ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{item.bobotDefault}%</span>
                <input
                  type="number"
                  step="0.1"
                  value={item.bobot || 0}
                  onChange={(e) => handleBobotChange(item.id, e.target.value)}
                  className={`w-20 px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 text-center ${
                    item.customBobot !== null ? 'border-orange-400 bg-orange-50' : 'border-gray-300'
                  }`}
                />
                <span className="text-gray-600">%</span>
              </div>
            ) : (
              <span className="font-semibold text-green-700">{item.bobot}%</span>
            )}
          </td>

          {/* PLAN START DATE */}
          <td className="px-4 py-3 text-sm">
            {!hasChildren && (
              <input
                type="date"
                value={item.planStartDate || ''}
                onChange={(e) => handleDateChange(item.id, 'planStartDate', e.target.value)}
                className="w-36 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
              />
            )}
          </td>

          {/* PLAN FINISH DATE */}
          <td className="px-4 py-3 text-sm">
            {!hasChildren && (
              <input
                type="date"
                value={item.planFinishDate || ''}
                onChange={(e) => handleDateChange(item.id, 'planFinishDate', e.target.value)}
                className="w-36 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
              />
            )}
          </td>
        </tr>

        {/* Render children recursively */}
        {hasChildren && item.children.map(child => renderMilestone(child, level + 1))}
      </React.Fragment>
    );
  };

  const totalBobot = calculateTotal();

  // ========================================
  // RENDER: Loading State
  // ========================================
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading milestone template...</p>
        </div>
      </div>
    );
  }

  // ========================================
  // RENDER: Error State
  // ========================================
  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-2xl font-bold text-gray-800">FO Segment Milestone Planning</h1>
          </div>
        </div>

        <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-800 mb-2">{error}</h3>
              <p className="text-red-700 mb-4">Unable to load milestone data. Please try again.</p>
              <button
                onClick={loadMilestoneData}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========================================
  // RENDER: Main Form
  // ========================================
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center gap-4">
          <button 
            onClick={onBack} 
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-800">
              {mode === 'create' ? '✨ Setup' : '📝 Edit'} FO Segment Milestones
            </h1>
            <p className="text-gray-600 mt-1">
              {segment.siteNameAB || segment.segmentName} - {project.projectCode}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Network className="w-6 h-6 text-green-600" />
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              mode === 'create' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-blue-100 text-blue-800'
            }`}>
              {mode === 'create' ? 'New Setup' : 'Edit Mode'}
            </span>
          </div>
        </div>
      </div>

      {/* Main Form Card */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        {/* Segment Profile Header */}
        <div className="p-6 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-t-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <Network className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Segment Profile</h3>
              <p className="text-green-100 text-sm">Complete milestone planning details</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-green-100 mb-1">Span ID</label>
              <p className="text-sm font-semibold">{segment.spanId}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-green-100 mb-1">Project Name</label>
              <p className="text-sm font-semibold">{project.projectName}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-green-100 mb-1">Segment Name</label>
              <p className="text-sm font-semibold">{segment.siteNameAB || segment.segmentName}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-green-100 mb-1">Length</label>
              <p className="text-sm font-semibold">{segment.commercialDistance} km</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-green-100 mb-1">Location</label>
              <p className="text-sm flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {segment.kota || '-'}
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-green-100 mb-1">Main Area</label>
              <p className="text-sm">{segment.mainArea || '-'}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-green-100 mb-1">Ring Type</label>
              <p className="text-sm">{segment.ringType || '-'}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-green-100 mb-1">Status</label>
              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-white bg-opacity-20">
                {segment.status || 'ACTIVE'}
              </span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6">
            {/* Info Banner */}
            <div className="mb-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-blue-800">Milestone Setup Instructions</p>
                  <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
                    <li>Default bobot shown in gray - adjust as needed</li>
                    <li>Orange background indicates customized bobot</li>
                    <li>Total bobot must equal 100%</li>
                    <li>Add planned start/finish dates for each milestone</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Milestone Table */}
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-green-600 to-green-500 text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">NO</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">MILESTONE</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">JENIS EVIDENCE</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Jumlah Evidence</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                      <div>BOBOT</div>
                      <div className="text-xs font-normal opacity-75">(default / custom)</div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Plan Start
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Plan Finish
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {milestones.map(milestone => renderMilestone(milestone))}
                  
                  {/* Total Row */}
                  <tr className="bg-gradient-to-r from-gray-100 to-gray-50 font-bold border-t-2 border-gray-300">
                    <td colSpan="4" className="px-4 py-3 text-right text-sm text-gray-700">
                      TOTAL BOBOT:
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-2">
                        {Math.abs(totalBobot - 100) < 0.01 ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        )}
                        <span className={`font-bold text-lg ${
                          Math.abs(totalBobot - 100) < 0.01 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {totalBobot.toFixed(2)}%
                        </span>
                      </div>
                    </td>
                    <td colSpan="2"></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Validation Warning/Success */}
            {Math.abs(totalBobot - 100) >= 0.01 ? (
              <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-800">Total Bobot Tidak Valid</p>
                  <p className="text-sm text-red-700 mt-1">
                    Total bobot harus 100%. Saat ini: <strong>{totalBobot.toFixed(2)}%</strong>. 
                    Silakan sesuaikan nilai bobot sebelum menyimpan.
                  </p>
                </div>
              </div>
            ) : (
              <div className="mt-4 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-green-800">Total Bobot Valid ✓</p>
                  <p className="text-sm text-green-700 mt-1">
                    Total bobot sudah mencapai 100%. Anda dapat menyimpan milestone ini.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end gap-3">
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all"
              disabled={isSubmitting || Math.abs(totalBobot - 100) >= 0.01}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {mode === 'create' ? 'Setup Milestone' : 'Update Milestone'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SegmentMilestoneForm;